import { userSettingsService } from "@/utils/api/userSettingsService";
import { useMutation, useQueryClient } from "@tanstack/vue-query";
import { toTypedSchema } from "@vee-validate/zod";
import type { PersonalData } from "~/utils/types";
import { personalDataSchema } from "~/utils/schemas";
import { useAppForm } from "~/composables/global/useAppForm";
import type { ResponseError } from "~/utils/responseError";

const FIELDS = Object.keys(personalDataSchema.shape) as Array<
  keyof PersonalData
>;

export default function usePersonalDataForm() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { data: user } = useUser();
  const updateEmail = ref(false);
  const updateUser = ref(false);

  const validationSchema = computed(() => toTypedSchema(personalDataSchema));

  const { form, handleSubmit, errors, resetForm } = useAppForm({
    fields: FIELDS,
    validationSchema: validationSchema,
  });

  watch(
    user,
    () => {
      if (user.value) {
        FIELDS.forEach((field) => {
          if (field === "password") return;
          form[field] = user.value[field];
        });
      }
    },
    { immediate: true },
  );

  watch(
    () => form.email,
    () => {
      if (form.email !== user.value?.email) {
        updateEmail.value = true;
      } else {
        updateEmail.value = false;
      }
    },
  );

  watch(
    () => [form.first_name, form.last_name],
    () => {
      if (
        form.first_name !== user.value?.first_name ||
        form.last_name !== user.value?.last_name
      ) {
        updateUser.value = true;
      } else {
        updateUser.value = false;
      }
    },
  );

  const { mutate: mutateUser, isPending: isPendingUser } = useMutation({
    mutationFn: (payload: UpdateUserSettingsForm) =>
      userSettingsService.updateUser(payload),
    onSuccess: () => {
      toast.add({
        severity: "success",
        detail: "Zmieniono dane osobowe.",
        life: 3000,
      });
      queryClient.invalidateQueries({ queryKey: ["user"] });
    },
    onError: (error: ResponseError) => {
      if (error.status === 422) {
        toast.add({
          severity: "error",
          detail: error.message,
          life: 3000,
        });
      }
    },
  });
  const { mutate: mutateEmail, isPending: isPendingEmail } = useMutation({
    mutationFn: (payload: UpdateEmailForm) =>
      userSettingsService.updateEmail(payload),
    onSuccess: () => {
      toast.add({
        severity: "success",
        detail:
          "Wysłano link potwierdzający zmianę adresu e-mail. Sprawdź swoją dotychczasową skrzynkę mailową.",
      });
      queryClient.invalidateQueries({ queryKey: ["user"] });
    },
    onError: (error: ResponseError) => {
      if (error.status === 422) {
        toast.add({
          severity: "error",
          detail: error.message,
          life: 3000,
        });
      }
    },
  });

  const handlePersonalDataChange = handleSubmit((values) => {
    if (Object.keys(errors.value).length > 0) return;

    if (updateUser.value) {
      mutateUser({
        first_name: values.first_name,
        last_name: values.last_name,
      });
    }
    if (updateEmail.value) {
      mutateEmail({
        email: values.email,
        email_confirmation: values.email,
        password: values.password,
      });
    }
  });

  return {
    updateEmail,
    updateUser,
    isPendingUser,
    isPendingEmail,
    form,
    handlePersonalDataChange,
    errors,
    resetForm,
  };
}
